/**
 * @file useDeliverySettings.ts
 * @description Пороги контроля отправлений для карточек заказа (phase19.13).
 *
 * Пороги нужны каждой карточке отгрузки, а меняются раз в полгода — тянуть их
 * отдельным запросом из каждой карточки нельзя. Поэтому один запрос на сессию
 * с модульным кэшем; форма настроек после сохранения кладёт свежие значения
 * сюда же (`primeDeliverySettings`), чтобы бейджи пересчитались без перезагрузки.
 */

import { useEffect, useState } from "react";

import {
  getDeliverySettings,
  type DeliverySettingsDto,
} from "../lib/api-delivery-monitoring";
import {
  DEFAULT_STALE_THRESHOLDS,
  type DeliveryStaleThresholds,
} from "../lib/shipment-state";

let cache: DeliverySettingsDto | null = null;
let inflight: Promise<DeliverySettingsDto> | null = null;
/** Подписчики хука — после `primeDeliverySettings` перерисовываем всех. */
const listeners = new Set<(value: DeliverySettingsDto) => void>();

/** Запрос настроек с кэшем: параллельные вызовы делят один промис. */
async function loadOnce(): Promise<DeliverySettingsDto> {
  if (cache) return cache;
  inflight ??= getDeliverySettings()
    .then((data) => {
      cache = data;
      return data;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Положить свежие настройки в кэш (после успешного PATCH в форме). */
export function primeDeliverySettings(next: DeliverySettingsDto): void {
  cache = next;
  for (const listener of listeners) listener(next);
}

/** Сбросить кэш — при смене активного контекста (магазина). */
export function resetDeliverySettingsCache(): void {
  cache = null;
}

/**
 * Пороги «зависшей» отгрузки. Пока настройки не пришли (или прав нет) —
 * дефолты схемы: бейдж лучше показать по умолчанию, чем не показать вовсе.
 */
export function useDeliveryThresholds(): DeliveryStaleThresholds {
  const [value, setValue] = useState<DeliverySettingsDto | null>(cache);

  useEffect(() => {
    let alive = true;
    listeners.add(setValue);
    void loadOnce()
      .then((data) => {
        if (alive) setValue(data);
      })
      .catch(() => {
        // Нет прав или сеть — молча живём на дефолтах, меню и карточки целы.
      });
    return () => {
      alive = false;
      listeners.delete(setValue);
    };
  }, []);

  return value ?? DEFAULT_STALE_THRESHOLDS;
}
